import React, { createContext, useContext, useState, useEffect } from 'react';
import type { FC, ReactNode } from 'react';
import type { WixConfig } from '@wix/cli-app-definitions';
import { useErrorReporter } from '@wix/cli-error-reporting';
import { readWixConfig } from './files/wix.config.js';
import type { DevCenterComponent } from './dev-center/components/dev-center-component.js';
import { loadDevCenterComponents } from './dev-center/components/load-dev-center-components.js';
import { ProjectModelWatcher } from './app-manifest/project-model-watcher.js';

export interface ProjectModel {
  config: WixConfig;
  projectFolder: string;
  components: DevCenterComponent[];
}

export async function loadProjectModel(
  projectFolder: string
): Promise<ProjectModel> {
  const config = await readWixConfig(projectFolder);

  return {
    projectFolder,
    config,
    components: await loadDevCenterComponents({
      projectFolder,
      config,
    }),
  };
}

interface ProjectModelState {
  model: ProjectModel;
  watcher: ProjectModelWatcher;
}

const ProjectModelContext = createContext<ProjectModelState | null>(null);

interface Props {
  watchForChanges?: boolean;
  value: ProjectModel;
  children: ReactNode;
}

export const ProjectModelProvider: FC<Props> = (props) => {
  const { reportError } = useErrorReporter();
  const [model, setModel] = useState(props.value);
  const [watcher] = useState(() => {
    const watcher = new ProjectModelWatcher(model);

    if (props.watchForChanges === true) {
      watcher.watch().onChange((newModel, changeType) => {
        if (changeType === 'model-updated') {
          setModel(newModel);
        }
      });
    }

    return watcher;
  });

  useEffect(() => {
    return () => {
      watcher.close().catch(reportError);
    };
  }, [watcher, reportError]);

  return (
    <ProjectModelContext.Provider value={{ model, watcher }}>
      {props.children}
    </ProjectModelContext.Provider>
  );
};

export function useProjectModel(): ProjectModelState {
  const context = useContext(ProjectModelContext);
  if (context == null) {
    throw new Error(
      'useProjectModel() cannot be used without an <ProjectModelProvider>'
    );
  }
  return context;
}
